import React, { useState, type FC } from 'react';
import { Icon } from 'src/components/widgets/icon';
import PlusIcon from 'src/shared/material_icons/content/add.svg';
import InfoIcon from 'src/shared/material_icons/action/info.svg';
import { Checkbox } from 'src/components/widgets/checkbox';
import { ZTooltip } from 'src/components/widgets/ztooltip';
import { T, backofficeUrl, toGlobalID } from 'src/shared/helpers';

import { CollapsibleSection } from '../common/collapsibleSection';
import { getToolInfo, type ResolvedToolCategory } from '../common/constants';
import {
    EmptyState,
    FilterBar,
    FilterBarLink,
    FooterGuideLink,
    GroupLabel,
    InheritedInfoIcon,
    ItemAction,
    ItemActionDanger,
    ItemDot,
    ItemMeta,
    ItemMetaRow,
    ItemName,
    ItemNameAction,
    ItemNameStack,
    ItemPreviewImg,
    LearnMoreLink,
    PanelBody,
    PanelItem,
    PanelItemChild,
    PanelItemDimmed,
    PanelItemSpaced,
    PanelItemSpacedLarge
} from '../common/styled';
import type { AddPanelKey, ProductToolData, SidePanelProduct, ToolDefinition } from '../common/types';
import { PanelTextSkeleton } from '../common/skeletons';

export interface ToolCategorySectionsProps {
    product: SidePanelProduct;
    toolData: ProductToolData;
    availableTools: ToolDefinition[];
    orderedCategories: ResolvedToolCategory[];
    hideFilterBar?: boolean;
    accountPrintMethods: Array<{ id: string; numericId: string; name: string }>;
    accountPricingRules: Array<{ id: string; name: string }>;
    accountHasRules: Record<string, boolean>;
    isToolAllowed: (tool: ToolDefinition) => boolean;
    expandAllKey: number;
    openSections: Set<string>;
    hideUnconfigured: boolean;
    isUnassigning: boolean;
    unassigningItemId: string | null;
    onOpenSectionChange: (toolKey: string, isOpen: boolean) => void;
    onHideUnconfiguredChange: (checked: boolean) => void;
    onCollapseAll: () => void;
    onOpenToolEditor: (toolKey: string, itemId: string) => void;
    onUnassignTool: (toolKey: string, itemId: string, itemName: string) => void;
    onDisableBulkVariation: () => void;
    onAssignShapes: () => void;
    onUnassignShapes: () => void;
    onShowAddPanel: (key: AddPanelKey) => void;
    onGoToFullTool: (route: string, id?: string) => void;
    onNavigate: (url: string, state?: any) => void;
    onEnablePlacement: (placementId: string, isEnabled: boolean) => void;
    onShowHelpPanel?: (url: string) => void;
}

const ToolCategorySections: FC<ToolCategorySectionsProps> = ({
    product,
    toolData,
    availableTools,
    orderedCategories,
    hideFilterBar,
    accountPrintMethods,
    accountPricingRules,
    accountHasRules,
    isToolAllowed,
    expandAllKey,
    openSections,
    hideUnconfigured,
    isUnassigning,
    unassigningItemId,
    onOpenSectionChange,
    onHideUnconfiguredChange,
    onCollapseAll,
    onOpenToolEditor,
    onUnassignTool,
    onDisableBulkVariation,
    onAssignShapes,
    onUnassignShapes,
    onShowAddPanel,
    onGoToFullTool,
    onNavigate,
    onEnablePlacement,
    onShowHelpPanel
}) => {
    const [expandedTools, setExpandedTools] = useState<Set<string>>(new Set());
    const renderNoPermission = () => <EmptyState>{T._("You don't have permission to access this tool.", 'Admin')}</EmptyState>;

    return (
        <PanelBody>
            {!hideFilterBar && product.zakekeEnvironment !== 'VIEWER' && (
                <FilterBar>
                    {openSections.size >= 3 && <FilterBarLink onClick={onCollapseAll}>{T._('Collapse all', 'Admin')}</FilterBarLink>}
                    <Checkbox label={T._('Hide unconfigured tools', 'Admin')} checked={hideUnconfigured} onChange={onHideUnconfiguredChange} />
                </FilterBar>
            )}
            {/* ===== TOOL CATEGORIES (ordered and grouped as in the navigation menu) ===== */}
            {orderedCategories.map((category) => {
                const categoryTools = category.toolKeys
                    .map((toolKey) => availableTools.find((t) => t.key === toolKey))
                    .filter((t): t is ToolDefinition => !!t)
                    .filter((t) => {
                        // Hide some tools for POD
                        if (product.provider !== 'ZAKEKE' && t.hiddenForPOD) return false;

                        // For COMPOSER, pricing is rendered inside ConfiguratorStructureSection
                        if (t.key === 'pricing' && product.zakekeEnvironment === 'COMPOSER') return false;

                        if (!isToolAllowed(t)) return !hideUnconfigured;

                        if (!hideUnconfigured) return true;

                        // Shapes: show when assigned directly or inherited via a printing method
                        // (library-only doesn't count as configured on this product).
                        if (t.key === 'shapes') return getToolInfo(product, t, toolData).count > 0;

                        const info = getToolInfo(product, t, toolData);
                        return info.count > 0 || !!info.status;
                    });
                if (categoryTools.length === 0) return null;
                return (
                    <React.Fragment key={category.key}>
                        {category.label && <GroupLabel>{category.label}</GroupLabel>}
                        {categoryTools.map((tool) => {
                            const info = getToolInfo(product, tool, toolData);
                            const toolAllowed = isToolAllowed(tool);
                            const sectionCount = toolAllowed ? info.count : 0;
                            // Ad-hoc rendering for pre-designed templates and pre-set configurations
                            if (tool.key === 'templates' || tool.key === 'preset-configurations') {
                                const isPresetConfigurations = tool.key === 'preset-configurations';
                                const navigateToTool = () => {
                                    if (tool.externalSetupUrl) {
                                        window.location.href = tool.externalSetupUrl(product.modelID.toString());
                                    } else {
                                        onGoToFullTool('/tools/template-product-selection/templates/' + product.modelID);
                                    }
                                };

                                const assignedSingularLabel = isPresetConfigurations
                                    ? T._('configuration assigned', 'Admin')
                                    : T._('template assigned', 'Admin');

                                const assignedPluralLabel = isPresetConfigurations
                                    ? T._('configurations assigned', 'Admin')
                                    : T._('templates assigned', 'Admin');

                                return (
                                    <CollapsibleSection
                                        key={`${tool.key}-${expandAllKey}`}
                                        title={tool.name}
                                        count={sectionCount}
                                        controlledOpen={openSections.has(tool.key)}
                                        helpUrl={tool.helpUrl}
                                        onHelpClick={tool.helpUrl && onShowHelpPanel ? () => onShowHelpPanel(tool.helpUrl!) : undefined}
                                        onOpenChange={(isOpen) => onOpenSectionChange(tool.key, isOpen)}
                                    >
                                        {!toolAllowed ? (
                                            renderNoPermission()
                                        ) : !toolData.loaded ? (
                                            <PanelTextSkeleton />
                                        ) : info.count > 0 ? (
                                            <PanelItem $clickable onClick={tool.fullModeOnlyInSidebar ? () => onOpenToolEditor(tool.key, '') : navigateToTool}>
                                                <ItemDot $color='#3b82f6' />
                                                <ItemName>
                                                    {info.count} {info.count === 1 ? assignedSingularLabel : assignedPluralLabel}
                                                </ItemName>
                                                <ItemAction>{T._('Manage', 'Admin')} &#8250;</ItemAction>
                                            </PanelItem>
                                        ) : (
                                            <>
                                                <EmptyState>
                                                    {tool.description}
                                                    {tool.helpUrl && (
                                                        <>
                                                            {' — '}
                                                            <LearnMoreLink
                                                                href={onShowHelpPanel ? undefined : tool.helpUrl}
                                                                target={onShowHelpPanel ? undefined : '_blank'}
                                                                rel={onShowHelpPanel ? undefined : 'noopener'}
                                                                as={onShowHelpPanel ? 'span' : undefined}
                                                                onClick={onShowHelpPanel ? () => onShowHelpPanel(tool.helpUrl!) : undefined}
                                                                style={onShowHelpPanel ? { cursor: 'pointer' } : undefined}
                                                            >
                                                                {T._('Learn more', 'Admin')} &#8250;
                                                            </LearnMoreLink>
                                                        </>
                                                    )}
                                                </EmptyState>
                                                <PanelItemSpacedLarge
                                                    $clickable
                                                    onClick={
                                                        tool.fullModeOnlyInSidebar
                                                            ? () => onOpenToolEditor(tool.key, tool.key === 'templates' ? '_new' : '')
                                                            : navigateToTool
                                                    }
                                                >
                                                    <ItemDot $color='#d1d5db' />
                                                    <ItemNameAction>
                                                        <Icon>
                                                            <PlusIcon />
                                                        </Icon>
                                                        {T._('Set up', 'Admin')}
                                                    </ItemNameAction>
                                                </PanelItemSpacedLarge>
                                            </>
                                        )}
                                    </CollapsibleSection>
                                );
                            }
                            // Ad-hoc rendering for images and clipart
                            if (tool.key === 'images') {
                                const restrictions = toolData.imageRestrictions || [];
                                const anyEnabled = restrictions.some((restriction) => restriction.clipartGalleries);
                                const allEnabled = restrictions.length > 0 && restrictions.every((restriction) => restriction.clipartGalleries);
                                const hasContent = !!toolData.hasGalleryContent;
                                const configured = anyEnabled && hasContent;
                                const enabledPMs = restrictions.filter((restriction) => restriction.clipartGalleries);
                                const disabledPMs = restrictions.filter((restriction) => !restriction.clipartGalleries);
                                const showPerPmBreakdown = hasContent && restrictions.length > 0 && !allEnabled;
                                return (
                                    <CollapsibleSection
                                        key={`${tool.key}-${expandAllKey}`}
                                        title={tool.name}
                                        count={toolAllowed && configured ? 1 : 0}
                                        isBoolean
                                        controlledOpen={openSections.has(tool.key)}
                                        helpUrl={tool.helpUrl}
                                        onHelpClick={tool.helpUrl && onShowHelpPanel ? () => onShowHelpPanel(tool.helpUrl!) : undefined}
                                        onOpenChange={(isOpen) => onOpenSectionChange(tool.key, isOpen)}
                                    >
                                        {!toolAllowed ? (
                                            renderNoPermission()
                                        ) : !toolData.loaded ? (
                                            <PanelTextSkeleton />
                                        ) : hasContent && allEnabled ? (
                                            <PanelItem $clickable onClick={() => onOpenToolEditor('images', '')}>
                                                <ItemDot $color='#3b82f6' />
                                                <ItemName>{T._('Configured', 'Admin')}</ItemName>
                                                <ItemAction>{T._('Manage', 'Admin')} &#8250;</ItemAction>
                                            </PanelItem>
                                        ) : showPerPmBreakdown ? (
                                            <>
                                                <PanelItem $clickable onClick={() => onOpenToolEditor('images', '')}>
                                                    <ItemDot $color={anyEnabled ? '#3b82f6' : '#f59e0b'} />
                                                    <ItemName>{T._('Configured', 'Admin')}</ItemName>
                                                    <ItemAction>{T._('Manage', 'Admin')} &#8250;</ItemAction>
                                                </PanelItem>
                                                <PanelItemChild>
                                                    <ItemMeta>
                                                        {T._("Gallery visibility depends on each printing method's settings", 'Admin')}
                                                        {' — '}
                                                        <LearnMoreLink
                                                            href={
                                                                onShowHelpPanel
                                                                    ? undefined
                                                                    : 'https://zakeke.zendesk.com/hc/en-us/articles/360007632300-Images-and-Clipart-Gallery#h_01FVSCWA4RQG3ZSGC0XPPSHVG7'
                                                            }
                                                            target={onShowHelpPanel ? undefined : '_blank'}
                                                            rel={onShowHelpPanel ? undefined : 'noopener'}
                                                            as={onShowHelpPanel ? 'span' : undefined}
                                                            onClick={
                                                                onShowHelpPanel
                                                                    ? () =>
                                                                          onShowHelpPanel(
                                                                              'https://zakeke.zendesk.com/hc/en-us/articles/360007632300-Images-and-Clipart-Gallery#h_01FVSCWA4RQG3ZSGC0XPPSHVG7'
                                                                          )
                                                                    : undefined
                                                            }
                                                            style={onShowHelpPanel ? { cursor: 'pointer' } : undefined}
                                                        >
                                                            {T._('Learn more', 'Admin')} &#8250;
                                                        </LearnMoreLink>
                                                    </ItemMeta>
                                                </PanelItemChild>
                                                {restrictions.map((restriction) => (
                                                    <PanelItemChild
                                                        key={restriction.printMethodId}
                                                        $clickable
                                                        onClick={() => onOpenToolEditor('printing-methods', toGlobalID(restriction.printMethodId, 'PrintType'))}
                                                    >
                                                        <ItemDot $color={restriction.clipartGalleries ? '#3b82f6' : '#f59e0b'} />
                                                        <ItemName>
                                                            {restriction.printMethodName}
                                                            <ItemMeta>
                                                                {restriction.clipartGalleries
                                                                    ? T._('Gallery enabled', 'Admin')
                                                                    : T._('Gallery disabled', 'Admin')}
                                                            </ItemMeta>
                                                        </ItemName>
                                                        <ItemAction>{T._('Edit', 'Admin')} &#8250;</ItemAction>
                                                    </PanelItemChild>
                                                ))}
                                            </>
                                        ) : (
                                            <>
                                                <EmptyState>
                                                    {tool.description}
                                                    {tool.helpUrl && (
                                                        <>
                                                            {' — '}
                                                            <LearnMoreLink
                                                                href={onShowHelpPanel ? undefined : tool.helpUrl}
                                                                target={onShowHelpPanel ? undefined : '_blank'}
                                                                rel={onShowHelpPanel ? undefined : 'noopener'}
                                                                as={onShowHelpPanel ? 'span' : undefined}
                                                                onClick={onShowHelpPanel ? () => onShowHelpPanel(tool.helpUrl!) : undefined}
                                                                style={onShowHelpPanel ? { cursor: 'pointer' } : undefined}
                                                            >
                                                                {T._('Learn more', 'Admin')} &#8250;
                                                            </LearnMoreLink>
                                                        </>
                                                    )}
                                                </EmptyState>
                                                <PanelItemSpacedLarge $clickable onClick={() => onOpenToolEditor(tool.key, '')}>
                                                    <ItemDot $color='#d1d5db' />
                                                    <ItemNameAction>
                                                        <Icon>
                                                            <PlusIcon />
                                                        </Icon>
                                                        {T._('Set up', 'Admin')}
                                                    </ItemNameAction>
                                                </PanelItemSpacedLarge>
                                            </>
                                        )}
                                    </CollapsibleSection>
                                );
                            }
                            // Ad-hoc rendering for Advanced Print File (shows individual files with enabled/disabled status)
                            if (tool.key === 'placement') {
                                const placements = toolData.placements || [];
                                return (
                                    <CollapsibleSection
                                        key={`${tool.key}-${expandAllKey}`}
                                        title={tool.name}
                                        count={toolAllowed ? placements.length : 0}
                                        controlledOpen={openSections.has(tool.key)}
                                        helpUrl={tool.helpUrl}
                                        onHelpClick={tool.helpUrl && onShowHelpPanel ? () => onShowHelpPanel(tool.helpUrl!) : undefined}
                                        onOpenChange={(isOpen) => onOpenSectionChange(tool.key, isOpen)}
                                    >
                                        {!toolAllowed ? (
                                            renderNoPermission()
                                        ) : !toolData.loaded ? (
                                            <PanelTextSkeleton />
                                        ) : placements.length > 0 ? (
                                            <>
                                                {placements.map((placement) => (
                                                    <PanelItem
                                                        key={placement.id}
                                                        $clickable
                                                        onClick={() =>
                                                            tool.buildEmbeddedUrl &&
                                                            onNavigate(backofficeUrl(tool.buildEmbeddedUrl(placement.id, product.modelID.toString())))
                                                        }
                                                    >
                                                        <ItemDot $color={placement.isEnabled ? '#16a34a' : '#f59e0b'} />
                                                        <ItemName>{placement.name || T._('Advanced Print File', 'Admin')}</ItemName>
                                                        {placement.isEnabled ? (
                                                            <ItemActionDanger
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    onEnablePlacement(placement.id, false);
                                                                }}
                                                            >
                                                                {T._('Disable', 'Admin')}
                                                            </ItemActionDanger>
                                                        ) : (
                                                            <ItemAction
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    onEnablePlacement(placement.id, true);
                                                                }}
                                                            >
                                                                {T._('Enable', 'Admin')}
                                                            </ItemAction>
                                                        )}
                                                        <ItemAction>{T._('Edit', 'Admin')} &#8250;</ItemAction>
                                                    </PanelItem>
                                                ))}
                                                <PanelItemSpaced
                                                    $clickable
                                                    onClick={() =>
                                                        tool.buildSetupRoute && onNavigate(backofficeUrl(tool.buildSetupRoute(product.modelID.toString())))
                                                    }
                                                >
                                                    <ItemDot $color='#d1d5db' />
                                                    <ItemNameAction>
                                                        <Icon>
                                                            <PlusIcon />
                                                        </Icon>
                                                        {T._('Add', 'Admin')}
                                                    </ItemNameAction>
                                                </PanelItemSpaced>
                                            </>
                                        ) : (
                                            <>
                                                <EmptyState>
                                                    {tool.description}
                                                    {tool.helpUrl && (
                                                        <>
                                                            {' — '}
                                                            {onShowHelpPanel ? (
                                                                <LearnMoreLink
                                                                    as='span'
                                                                    onClick={() => onShowHelpPanel(tool.helpUrl!)}
                                                                    style={{ cursor: 'pointer' }}
                                                                >
                                                                    {T._('Learn more', 'Admin')} &#8250;
                                                                </LearnMoreLink>
                                                            ) : (
                                                                <LearnMoreLink href={tool.helpUrl} target='_blank' rel='noopener'>
                                                                    {T._('Learn more', 'Admin')} &#8250;
                                                                </LearnMoreLink>
                                                            )}
                                                        </>
                                                    )}
                                                </EmptyState>
                                                <PanelItemSpacedLarge
                                                    $clickable
                                                    onClick={() =>
                                                        tool.buildSetupRoute && onNavigate(backofficeUrl(tool.buildSetupRoute(product.modelID.toString())))
                                                    }
                                                >
                                                    <ItemDot $color='#d1d5db' />
                                                    <ItemNameAction>
                                                        <Icon>
                                                            <PlusIcon />
                                                        </Icon>
                                                        {T._('Set up', 'Admin')}
                                                    </ItemNameAction>
                                                </PanelItemSpacedLarge>
                                            </>
                                        )}
                                    </CollapsibleSection>
                                );
                            }
                            const isPrintingMethods = tool.key === 'printing-methods';
                            const isPricing = tool.key === 'pricing';
                            const isComposerPricing = isPricing && product.zakekeEnvironment === 'COMPOSER';
                            const handleAddClick = () => {
                                if (tool.externalSetupUrl) {
                                    window.location.href = tool.externalSetupUrl(product.modelID.toString());
                                } else if ((isPrintingMethods || isPricing) && tool.addPanelKey && accountHasRules[tool.key]) {
                                    onShowAddPanel(tool.addPanelKey);
                                } else if (isPrintingMethods || isPricing) {
                                    onOpenToolEditor(tool.key, '');
                                } else if (tool.addPanelKey && accountHasRules[tool.key]) {
                                    onShowAddPanel(tool.addPanelKey);
                                } else if (tool.isProductSpecific && tool.buildSetupRoute) {
                                    onNavigate(backofficeUrl(tool.buildSetupRoute(product.modelID.toString())));
                                } else if (tool.inlineCreate) {
                                    onOpenToolEditor(tool.key, '');
                                } else if (tool.fullModeOnlyInSidebar) {
                                    onOpenToolEditor(tool.key, '');
                                } else {
                                    onGoToFullTool(tool.route);
                                }
                            };
                            const hasItems = info.items.length > 0;
                            const canAddMore = (!!tool.addPanelKey || !!tool.buildSetupRoute) && !(tool.isSingleRule && hasItems);
                            return (
                                <CollapsibleSection
                                    key={`${tool.key}-${expandAllKey}`}
                                    title={tool.name}
                                    count={sectionCount}
                                    isBoolean={tool.isBoolean}
                                    controlledOpen={openSections.has(tool.key)}
                                    helpUrl={tool.helpUrl}
                                    onHelpClick={tool.helpUrl && onShowHelpPanel ? () => onShowHelpPanel(tool.helpUrl!) : undefined}
                                    onOpenChange={(isOpen) => onOpenSectionChange(tool.key, isOpen)}
                                >
                                    {!toolAllowed ? (
                                        renderNoPermission()
                                    ) : !toolData.loaded ? (
                                        <PanelTextSkeleton />
                                    ) : hasItems ? (
                                        <>
                                            {isComposerPricing && (
                                                <EmptyState>
                                                    {T._('These rules apply to the design part.', 'Admin')}{' '}
                                                    {T._('For pricing the configuration part, follow', 'Admin')}{' '}
                                                    <LearnMoreLink
                                                        href={
                                                            onShowHelpPanel
                                                                ? undefined
                                                                : 'https://zakeke.zendesk.com/hc/en-us/articles/360009576134-Composer#h_01G0M6P43K3A6F8NHZBFVHP02J'
                                                        }
                                                        target={onShowHelpPanel ? undefined : '_blank'}
                                                        rel={onShowHelpPanel ? undefined : 'noopener'}
                                                        as={onShowHelpPanel ? 'span' : undefined}
                                                        onClick={
                                                            onShowHelpPanel
                                                                ? () =>
                                                                      onShowHelpPanel(
                                                                          'https://zakeke.zendesk.com/hc/en-us/articles/360009576134-Composer#h_01G0M6P43K3A6F8NHZBFVHP02J'
                                                                      )
                                                                : undefined
                                                        }
                                                        style={onShowHelpPanel ? { cursor: 'pointer' } : undefined}
                                                    >
                                                        {T._('this guide', 'Admin')} &#8250;
                                                    </LearnMoreLink>
                                                </EmptyState>
                                            )}
                                            {tool.key === 'get-a-quote'
                                                ? info.items.map((item) => {
                                                      const isItemUnassigning = isUnassigning && unassigningItemId === item.id;
                                                      return (
                                                          <PanelItemDimmed
                                                              key={item.id}
                                                              $clickable={!isItemUnassigning}
                                                              onClick={() => !isItemUnassigning && onOpenToolEditor(tool.key, item.id)}
                                                              $dimmed={isItemUnassigning}
                                                          >
                                                              <ItemDot $color='#3b82f6' />
                                                              <ItemName>{item.name}</ItemName>
                                                              <ItemActionDanger
                                                                  onClick={(e) => {
                                                                      e.stopPropagation();
                                                                      onUnassignTool(tool.key, item.id, item.name);
                                                                  }}
                                                              >
                                                                  {T._('Unassign', 'Admin')}
                                                              </ItemActionDanger>
                                                              <ItemAction>{T._('Edit', 'Admin')} &#8250;</ItemAction>
                                                          </PanelItemDimmed>
                                                      );
                                                  })
                                                : (expandedTools.has(tool.key) ? info.items : info.items.slice(0, 10)).map((item) => {
                                                      // Rules inherited from a printing method are read-only: show the rule name and
                                                      // the printing method it comes from, without Edit/Unassign.
                                                      if (item.inherited) {
                                                          return (
                                                              <PanelItem key={item.id}>
                                                                  <ItemDot $color='#16a34a' />
                                                                  <ItemNameStack>
                                                                      <span>{item.name}</span>
                                                                      {item.meta && (
                                                                          <ItemMetaRow>
                                                                              <ItemMeta>{item.meta}</ItemMeta>
                                                                              <ZTooltip
                                                                                  position='top'
                                                                                  interactive
                                                                                  title={T._(
                                                                                      "This rule isn't assigned directly to the product — it applies because it's associated with one of the product's printing methods. To edit or remove it, change or unassign the rule on that printing method.",
                                                                                      'Admin'
                                                                                  )}
                                                                              >
                                                                                  <InheritedInfoIcon aria-label={T._('What does inherited mean?', 'Admin')}>
                                                                                      <InfoIcon />
                                                                                  </InheritedInfoIcon>
                                                                              </ZTooltip>
                                                                          </ItemMetaRow>
                                                                      )}
                                                                  </ItemNameStack>
                                                              </PanelItem>
                                                          );
                                                      }
                                                      const isItemUnassigning = isUnassigning && unassigningItemId === item.id;
                                                      return (
                                                          <PanelItemDimmed
                                                              key={item.id}
                                                              $clickable={!isItemUnassigning}
                                                              onClick={() => !isItemUnassigning && onOpenToolEditor(tool.key, item.id)}
                                                              $dimmed={isItemUnassigning}
                                                          >
                                                              {item.previewUrl ? (
                                                                  <ItemPreviewImg src={item.previewUrl} alt={item.name} />
                                                              ) : (
                                                                  <ItemDot $color='#3b82f6' />
                                                              )}
                                                              <ItemName>{item.name}</ItemName>
                                                              <ItemActionDanger
                                                                  onClick={(e) => {
                                                                      e.stopPropagation();
                                                                      onUnassignTool(tool.key, item.id, item.name);
                                                                  }}
                                                              >
                                                                  {T._('Unassign', 'Admin')}
                                                              </ItemActionDanger>
                                                              <ItemAction>{T._('Edit', 'Admin')} &#8250;</ItemAction>
                                                          </PanelItemDimmed>
                                                      );
                                                  })}
                                            {info.items.length > 10 && (
                                                <PanelItem
                                                    $clickable
                                                    onClick={() =>
                                                        setExpandedTools((prev) => {
                                                            const next = new Set(prev);
                                                            if (next.has(tool.key)) next.delete(tool.key);
                                                            else next.add(tool.key);
                                                            return next;
                                                        })
                                                    }
                                                >
                                                    <ItemMeta>
                                                        {expandedTools.has(tool.key)
                                                            ? T._('Show less', 'Admin')
                                                            : `+${info.items.length - 10} ${T._('more', 'Admin')}`}
                                                    </ItemMeta>
                                                </PanelItem>
                                            )}
                                            {canAddMore && (
                                                <PanelItemSpaced
                                                    $clickable
                                                    onClick={isPrintingMethods || isPricing ? () => onShowAddPanel(tool.addPanelKey!) : handleAddClick}
                                                >
                                                    <ItemDot $color='#d1d5db' />
                                                    <ItemNameAction>
                                                        <Icon>
                                                            <PlusIcon />
                                                        </Icon>
                                                        {T._('Add', 'Admin')}
                                                    </ItemNameAction>
                                                </PanelItemSpaced>
                                            )}
                                            {['get-a-quote', 'collage', 'area-background', 'ai-lifestyle-scenes', 'ai-try-on', 'shapes', 'images'].includes(
                                                tool.key
                                            ) && (
                                                <PanelItemSpaced
                                                    $clickable
                                                    onClick={() => {
                                                        if (tool.key === 'ai-lifestyle-scenes' || tool.key === 'ai-try-on') {
                                                            onGoToFullTool(tool.route + '?tab=usage-controls');
                                                        } else if (tool.key === 'collage') {
                                                            onGoToFullTool('/tools/collage-layouts/gallery');
                                                        } else if (tool.key === 'area-background') {
                                                            onGoToFullTool('/tools/fill-background?tab=gallery');
                                                        } else {
                                                            onGoToFullTool(tool.route);
                                                        }
                                                    }}
                                                >
                                                    <ItemNameAction
                                                        style={
                                                            tool.key === 'ai-lifestyle-scenes' ||
                                                            tool.key === 'ai-try-on' ||
                                                            tool.key === 'get-a-quote' ||
                                                            tool.key === 'collage' ||
                                                            tool.key === 'area-background'
                                                                ? { fontSize: '12px' }
                                                                : undefined
                                                        }
                                                    >
                                                        {tool.key === 'ai-lifestyle-scenes' || tool.key === 'ai-try-on'
                                                            ? T._('Go to Usage Controls', 'Admin')
                                                            : tool.key === 'get-a-quote'
                                                              ? T._('Go to Tool Settings', 'Admin')
                                                              : tool.key === 'collage'
                                                                ? T._('Go to Collage Layout Gallery', 'Admin')
                                                                : tool.key === 'area-background'
                                                                  ? T._('Go to Background Gallery', 'Admin')
                                                                  : T._('Manage tool', 'Admin')}{' '}
                                                        &#8250;
                                                    </ItemNameAction>
                                                </PanelItemSpaced>
                                            )}
                                        </>
                                    ) : info.status ? (
                                        <PanelItem
                                            $clickable
                                            onClick={() => {
                                                if (tool.externalSetupUrl) {
                                                    window.location.href = tool.externalSetupUrl(product.modelID.toString());
                                                } else if (tool.fullModeOnly && tool.buildSetupRoute) {
                                                    onNavigate(backofficeUrl(tool.buildSetupRoute(product.modelID.toString())));
                                                } else if ((tool.dedicatedEditor || tool.inlineCreate) && tool.buildEmbeddedUrl) {
                                                    onOpenToolEditor(tool.key, '_manage');
                                                } else if (tool.buildEmbeddedUrl) {
                                                    onOpenToolEditor(tool.key, '');
                                                } else if (tool.buildSetupRoute) {
                                                    onNavigate(backofficeUrl(tool.buildSetupRoute(product.modelID.toString())));
                                                } else if (tool.quickModeOnly) {
                                                    onOpenToolEditor(tool.key, '');
                                                } else {
                                                    onGoToFullTool(tool.route);
                                                }
                                            }}
                                        >
                                            <ItemDot $color={tool.key === 'shapes' && !toolData.shapesConfigured ? '#f59e0b' : '#3b82f6'} />
                                            <ItemName>{info.status}</ItemName>
                                            {tool.key === 'bulk-variations' && (
                                                <ItemActionDanger
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onDisableBulkVariation();
                                                    }}
                                                >
                                                    {T._('Disable', 'Admin')}
                                                </ItemActionDanger>
                                            )}
                                            {tool.key === 'shapes' && toolData.shapesConfigured && (
                                                <ItemActionDanger
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onUnassignShapes();
                                                    }}
                                                >
                                                    {T._('Unassign', 'Admin')}
                                                </ItemActionDanger>
                                            )}
                                            {tool.key === 'shapes' && !toolData.shapesConfigured ? (
                                                <ItemAction
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onAssignShapes();
                                                    }}
                                                >
                                                    {T._('Assign', 'Admin')}
                                                </ItemAction>
                                            ) : (
                                                <ItemAction>{T._('Manage', 'Admin')} &#8250;</ItemAction>
                                            )}
                                        </PanelItem>
                                    ) : (
                                        <>
                                            <EmptyState>
                                                {isComposerPricing ? (
                                                    <>
                                                        {T._('Create price markup for the design part of your product.', 'Admin')}{' '}
                                                        {T._('For pricing the configuration part, follow', 'Admin')}{' '}
                                                        <LearnMoreLink
                                                            href={
                                                                onShowHelpPanel
                                                                    ? undefined
                                                                    : 'https://zakeke.zendesk.com/hc/en-us/articles/360009576134-Composer#h_01G0M6P43K3A6F8NHZBFVHP02J'
                                                            }
                                                            target={onShowHelpPanel ? undefined : '_blank'}
                                                            rel={onShowHelpPanel ? undefined : 'noopener'}
                                                            as={onShowHelpPanel ? 'span' : undefined}
                                                            onClick={
                                                                onShowHelpPanel
                                                                    ? () =>
                                                                          onShowHelpPanel(
                                                                              'https://zakeke.zendesk.com/hc/en-us/articles/360009576134-Composer#h_01G0M6P43K3A6F8NHZBFVHP02J'
                                                                          )
                                                                    : undefined
                                                            }
                                                            style={onShowHelpPanel ? { cursor: 'pointer' } : undefined}
                                                        >
                                                            {T._('this guide', 'Admin')} &#8250;
                                                        </LearnMoreLink>
                                                    </>
                                                ) : (
                                                    <>
                                                        {tool.description}
                                                        {tool.helpUrl && (
                                                            <>
                                                                {' — '}
                                                                {onShowHelpPanel ? (
                                                                    <LearnMoreLink
                                                                        as='span'
                                                                        onClick={() => onShowHelpPanel(tool.helpUrl!)}
                                                                        style={{ cursor: 'pointer' }}
                                                                    >
                                                                        {T._('Learn more', 'Admin')} &#8250;
                                                                    </LearnMoreLink>
                                                                ) : (
                                                                    <LearnMoreLink href={tool.helpUrl} target='_blank' rel='noopener'>
                                                                        {T._('Learn more', 'Admin')} &#8250;
                                                                    </LearnMoreLink>
                                                                )}
                                                            </>
                                                        )}
                                                    </>
                                                )}
                                            </EmptyState>
                                            <PanelItemSpacedLarge $clickable onClick={handleAddClick}>
                                                <ItemDot $color='#d1d5db' />
                                                <ItemNameAction>
                                                    +{' '}
                                                    {tool.addPanelKey && accountHasRules[tool.key]
                                                        ? T._('Add', 'Admin')
                                                        : tool.key === 'bulk-variations' ||
                                                            tool.key === 'ai-image-generation' ||
                                                            tool.key === 'ai-design-generation'
                                                          ? T._('Enable', 'Admin')
                                                          : T._('Set up', 'Admin')}
                                                </ItemNameAction>
                                            </PanelItemSpacedLarge>
                                        </>
                                    )}
                                </CollapsibleSection>
                            );
                        })}
                    </React.Fragment>
                );
            })}
        </PanelBody>
    );
};

export default ToolCategorySections;
